import React, { useState, useEffect, useRef } from 'react';
import GlassCard from '../../common/GlassCard/GlassCard';
import Button from '../../common/Button/Button';
import CodeAnimation from './CodeAnimation';
import { useModal } from '../../common/../../context/ModalContext';
import styles from './HeroConsole.module.css';

/* ── Notifications ───────────────────────────────────────────── */

const NOTIFICATIONS = [
  { id: 'n1', icon: '✅', title: 'Order confirmed',     sub: '#4821 — processed'     },
  { id: 'n2', icon: '📦', title: 'Shipment dispatched', sub: 'Arrives Thursday'       },
  { id: 'n3', icon: '💬', title: 'New message',         sub: 'Sarah: "Looks great!"'  },
  { id: 'n4', icon: '📊', title: 'Report ready',        sub: 'Monthly summary'        },
  { id: 'n5', icon: '🔔', title: 'Reminder',            sub: 'Meeting at 3:00 pm'     },
];

const NotificationFeed = ({ active }) => {
  const [items, setItems] = useState([]);
  const indexRef = useRef(0);

  useEffect(() => {
    if (!active) return;

    const add = () => {
      const notif = { ...NOTIFICATIONS[indexRef.current], key: Date.now() };
      setItems(prev => [...prev.slice(-6), notif]);
      indexRef.current = (indexRef.current + 1) % NOTIFICATIONS.length;
    };

    add(); // first notification immediately on app load
    const interval = setInterval(add, 2200);
    return () => clearInterval(interval);
  }, [active]);

  return (
    <div className={styles.notificationZone}>
      <div className={styles.notificationList}>
        {items.map(item => (
          <div key={item.key} className={styles.notification}>
            <span className={styles.notifIcon}>{item.icon}</span>
            <div className={styles.notifContent}>
              <span className={styles.notifTitle}>{item.title}</span>
              <span className={styles.notifSub}>{item.sub}</span>
            </div>
            <span className={styles.notifTime}>now</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Mini Dashboard ──────────────────────────────────────────── */

const BARS = [
  { label: 'Jan', h: 52,  delay: 0.00 },
  { label: 'Feb', h: 74,  delay: 0.12 },
  { label: 'Mar', h: 60,  delay: 0.24 },
  { label: 'Apr', h: 92,  delay: 0.36, peak: true },
  { label: 'May', h: 70,  delay: 0.48 },
  { label: 'Jun', h: 104, delay: 0.60, peak: true },
];

const MiniDashboard = ({ active }) => {
  const [growing, setGrowing] = useState(false);

  useEffect(() => {
    if (!active) return;

    let t1, t2;
    const cycle = () => {
      setGrowing(true);
      t1 = setTimeout(() => {
        setGrowing(false);
        t2 = setTimeout(cycle, 700);
      }, 3200);
    };

    const t3 = setTimeout(cycle, 400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [active]);

  return (
    <div className={styles.dashboard}>
      <div className={styles.dashHeader}>
        <span className={styles.dashTitle}>Monthly Revenue</span>
        <span className={styles.dashGrowth}>↑ 24%</span>
      </div>
      <div className={styles.chartBars}>
        {BARS.map((bar, i) => (
          <div key={i} className={styles.barWrapper}>
            <div
              className={`${styles.bar} ${bar.peak ? styles.barPeak : ''} ${growing ? styles.barGrow : ''}`}
              style={{
                height: `${bar.h}px`,
                transitionDelay: growing ? `${bar.delay}s` : '0s',
              }}
            />
            <span className={styles.barLabel}>{bar.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Phone Content ───────────────────────────────────────────── */

const PhoneContent = ({ active }) => {
  const [phase, setPhase] = useState('splash'); // 'splash' | 'app'

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => setPhase('app'), 3000);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <div className={styles.phoneContent}>
      {/* Logo — always visible */}
      <img
        src="/images/logo/TEXT%20LIGHT.png"
        alt="Kilonova Technologies"
        className={styles.splashLogo}
      />

      {/* Phase area — crossfades between splash and app */}
      <div className={styles.phaseArea}>

        {/* Splash: "Beautiful Apps Built Here" */}
        <div className={`${styles.splashPhase} ${phase === 'app' ? styles.phaseOut : ''}`}>
          <p className={styles.splashText}>
            Beautiful Apps<br />Built Here
          </p>
        </div>

        {/* App: notifications + chart */}
        <div className={`${styles.appPhase} ${phase === 'app' ? styles.phaseIn : ''}`}>
          <NotificationFeed active={phase === 'app'} />
          <MiniDashboard active={phase === 'app'} />
        </div>

      </div>
    </div>
  );
};

/* ── Hero Console ────────────────────────────────────────────── */

const HeroConsole = () => {
  const hasHash = window.location.hash !== '';
  const isNavigation = sessionStorage.getItem('kilonova-boot-seen') === 'true' &&
                       performance.getEntriesByType('navigation')[0]?.type === 'navigate';

  const [showConsole, setShowConsole] = useState(hasHash || isNavigation);
  const { openModal } = useModal();

  const scrollToPortfolio = () => {
    document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAnimationComplete = () => {
    sessionStorage.setItem('kilonova-boot-seen', 'true');
    setTimeout(() => setShowConsole(true), 300);
  };

  return (
    <section className={styles.heroSection}>
      <div className={styles.gradientBackground}></div>
      {!showConsole && <CodeAnimation onComplete={handleAnimationComplete} />}

      <div className={styles.consoleWrapper}>
        <GlassCard
          padding="lg"
          className={`${styles.heroConsole} ${showConsole ? styles.visible : ''}`}
        >
          <div className={styles.consoleContent}>

            {/* Left Side */}
            <div className={styles.textContent}>
              <h1 className={styles.mainHeadline}>
                Transforming Business Challenges Into{' '}
                <span className={styles.highlight}>Digital Solutions</span>
              </h1>
              <p className={styles.subheadline}>
                We build custom software that solves real problems. From web applications
                to mobile apps, we turn your vision into powerful, scalable solutions.
              </p>
              <div className={styles.ctaButtons}>
                <Button variant="primary" size="lg" onClick={openModal}>
                  Start Your Project
                </Button>
                <Button variant="outline" size="lg" onClick={scrollToPortfolio}>
                  View Our Work
                </Button>
              </div>
              <div className={styles.trustIndicators}>
                <div className={styles.indicator}>
                  <span className={styles.indicatorNumber}>50+</span>
                  <span className={styles.indicatorLabel}>Projects Delivered</span>
                </div>
                <div className={styles.indicator}>
                  <span className={styles.indicatorNumber}>95%</span>
                  <span className={styles.indicatorLabel}>Client Satisfaction</span>
                </div>
                <div className={styles.indicator}>
                  <span className={styles.indicatorNumber}>24/7</span>
                  <span className={styles.indicatorLabel}>Support Available</span>
                </div>
              </div>
            </div>

            {/* Right Side: Phone */}
            <div className={styles.visualContent}>
              <div className={styles.phoneMockup}>
                <div className={styles.phoneFrame}>
                  <div className={styles.phoneNotch}></div>
                  <div className={styles.phoneScreen}>
                    <PhoneContent active={showConsole} />
                  </div>
                </div>
                <div className={`${styles.floatingElement} ${styles.element1}`}>⚛️</div>
                <div className={`${styles.floatingElement} ${styles.element2}`}>🚀</div>
                <div className={`${styles.floatingElement} ${styles.element3}`}>💡</div>
              </div>
            </div>

          </div>
        </GlassCard>
      </div>
    </section>
  );
};

export default HeroConsole;
